// src/pages/RecapPage.jsx
import * as React from 'react';
import { useNavigate, Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Button, Divider, Chip, Stack, CircularProgress, Alert
} from '@mui/material';

import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { api } from '../lib/api';
import { isLoggedIn } from '../lib/auth';
import {
  readCart,
  syncCartToServer,
  attachCartToUser,
  writeCart,
  writeCartMeta
} from '../lib/cartSession';

// 🔐 Modals auth
import LoginModal from '../components/auth/LoginModal';
import RegisterModal from '../components/auth/RegisterModal';
import ForgotPasswordModal from '../components/auth/ForgotPasswordModal';

// ✅ Toast stylé
import FancyToast from '../components/common/FancyToast';

// i18n
import { useI18n } from '../i18n/I18nProvider';
import recapI18n from '../i18n/pages/recappage.i18n';

const GREEN = '#0466c8';
const GREEN_BG = 'rgba(78, 67, 160, 0.08)';
const money = (n) => Number(n || 0).toFixed(2);

const strip = (s = '') => String(s).replace(/\s+/g, ' ').trim();

function normalizeComponentPrefix(raw = '', t) {
  // ✅ compat vieux navigateurs : pas de \p{Diacritic}
  let base = '';
  try {
    base = String(raw)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // <-- safe
      .toLowerCase()
      .trim();
  } catch {
    base = String(raw).toLowerCase().trim();
  }

  // fallback propre
  const fallback = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

  if (!base) return t('recap.labels.screen', 'Écran');

  // ✅ Écran
  if (base.includes('ecran') || base.includes('screen') || base.includes('lcd') || base.includes('display')) {
    return t('recap.labels.screen', 'Écran');
  }

  // ✅ Carte mère
  if (base.includes('carte mere') || base.includes('carte mere') || base.includes('motherboard') || base.includes('mainboard')) {
    return t('recap.labels.motherboard', 'Carte mère');
  }

  // ✅ Batterie
  if (base.includes('batterie') || base.includes('battery')) {
    return t('recap.labels.battery', 'Batterie');
  }

  // ✅ Caméra
  if (base.includes('camera') || base.includes('camera module') || base.includes('cam')) {
    return t('recap.labels.camera', 'Caméra');
  }

  // ✅ Haut-parleur
  if ((base.includes('haut') && base.includes('parleur')) || base.includes('speaker') || base.includes('loudspeaker')) {
    return t('recap.labels.speaker', 'Haut-parleur');
  }

  // ✅ Micro
  if (base.includes('microphone') || base.includes('micro')) {
    return t('recap.labels.microphone', 'Micro');
  }

  // ✅ Connecteur de charge
  if (base.includes('connecteur') || base.includes('charge') || base.includes('charging') || base.includes('dock') || base.includes('usb')) {
    return t('recap.labels.chargingConnector', 'Connecteur de charge');
  }

  // ✅ Bouton
  if (base.includes('bouton') || base.includes('button')) {
    return t('recap.labels.button', 'Bouton');
  }

  // ✅ Vibreur
  if (base.includes('vibreur') || base.includes('taptic') || base.includes('vibrator')) {
    return t('recap.labels.vibrator', 'Vibreur');
  }

  return fallback(strip(raw));
}
function formatRecapLabel(line, t) {
  const id = String(line?.id || '');
  const raw = String(line?.label || '');

  // ✅ Détection robuste “batch sort” (la DB peut renvoyer un label en dur, pas forcément un id brand:)
  const normalizeBatch = (s = '') => {
    try {
      return String(s)
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // enlève accents
        .toLowerCase()
        .replace(/[’']/g, "'")
        .replace(/[^a-z0-9\s!]/g, ' ') // garde !
        .replace(/\s+/g, ' ')
        .trim();
    } catch {
      return String(s).toLowerCase().replace(/\s+/g, ' ').trim();
    }
  };

  const lowBatch = normalizeBatch(raw);

  // ✅ Détection multi-langue (FR/EN/ES/PL)
  const isBatchSort =
    // FR
    lowBatch.includes('vendez en 1 clic') ||
    lowBatch.includes('envoie') ||
    lowBatch.includes('on trie pour toi') ||
    // EN
    lowBatch.includes('send it') ||
    lowBatch.includes("we'll sort") ||
    lowBatch.includes('well sort') ||
    lowBatch.includes('sort it for you') ||
    // ES
    lowBatch.includes('envialo') ||
    lowBatch.includes('lo clasificamos') ||
    lowBatch.includes('clasificamos por ti') ||
    // PL
    lowBatch.includes('wyslij') ||
    lowBatch.includes('posortujemy') ||
    lowBatch.includes('za ciebie');

  const parts = raw.includes('—') ? raw.split('—').map(strip) : [strip(raw)];
  const screenLabel = t('recap.labels.screen', 'Écran');

  let baseLabel;

  if (isBatchSort) {
    baseLabel = t('recap.labels.batchSort', 'Envoie, on trie pour toi !');
  } else if (id.startsWith('model:')) {
    const left = parts[0] || raw.replace(/—\s*Appareil complet$/i, '').trim();
    baseLabel = `${screenLabel} — ${left}`;
  } else if (id.startsWith('component:')) {
    if (parts.length >= 2) {
      const leftBrandModel = parts[0];
      const rightComponent = parts.slice(1).join(' — ');
      const prefix = normalizeComponentPrefix(rightComponent, t);
      baseLabel = `${prefix} — ${leftBrandModel}`;
    } else {
      baseLabel = raw;
    }
  } else if (id.startsWith('brand:')) {
    // batch-sort déjà géré au-dessus
    baseLabel = raw ? raw.charAt(0).toUpperCase() + raw.slice(1) : raw;
  } else {
    baseLabel = raw;
  }

  const note = line?.expertiseNote;

  // ✨ Normalisation de l’affichage pièces / poids / colis (expertise)
  if (note != null) {
    const packagesLabel = t('recap.labels.packages', 'colis');
    const piecesLabel = t('recap.labels.pieces', 'pièces');

    // ✅ "kg" est universel : on le garde en dur pour toutes les langues
    const kgLabel = 'kg';

    if (typeof note === 'object') {
      // ✅ Backward-compatible keys (NEW + legacy)
      const packagesRaw =
        note.parcelsCount ??
        note.parcels ??
        note.packagesCount ??
        note.packages ??
        note.colis;

      const weightRaw = note.weightKg ?? note.weight;
      const piecesRaw = note.piecesCount ?? note.pieces;

      const piecesStr =
        piecesRaw != null && String(piecesRaw).trim() !== ''
          ? `${String(piecesRaw).trim()} ${piecesLabel}`
          : '';

      const weightStr =
        weightRaw != null && String(weightRaw).trim() !== ''
          ? `${String(weightRaw).trim()} ${kgLabel}`
          : '';

      const packagesStr =
        packagesRaw != null && String(packagesRaw).trim() !== ''
          ? `${String(packagesRaw).trim()} ${packagesLabel}`
          : '';

      // ✅ Ordre UX: pièces · poids · colis
      const partsNote = [piecesStr, weightStr, packagesStr].filter(Boolean);

      if (partsNote.length > 0) {
        return `${baseLabel} (${partsNote.join(' · ')})`;
      }
    }

    const trimmed = String(note).trim();
    if (trimmed) {
      return `${baseLabel} (${trimmed})`;
    }
  }

  return baseLabel;
}

export default function RecapPage() {
// APRÈS
  const nav = useNavigate();
  const location = useLocation();
  const editingTransactionId = location.state?.editingTransactionId || null; // 👈 ajout

  const { t, register } = useI18n();
  React.useEffect(() => { register('recap', recapI18n); }, [register]);

  const [loading, setLoading] = React.useState(true);
  const [err, setErr] = React.useState('');
  const [selectedLines, setSelectedLines] = React.useState([]);

  const [submitting, setSubmitting] = React.useState(false);

  // ⬇️ contrôles modals auth
  const [loginOpen, setLoginOpen] = React.useState(false);
  const [registerOpen, setRegisterOpen] = React.useState(false);
  const [forgotOpen, setForgotOpen] = React.useState(false);

  const [toast, setToast] = React.useState({ open: false, titleKey: '', subtitleKey: '', kind: 'success' });

  // Notes d'expertise transmises depuis /tarifs (TarifsRecap)
  const expertiseNotesFromNav = location?.state?.summary?.expertiseNotes || null;

  // Charge le référentiel et reconstruit les lignes à partir du panier local
  React.useEffect(() => {
    let mounted = true;
    (async () => {
      setErr('');
      setLoading(true);
      try {
        const cart = readCart(); // { [id]: qty }

        const [brandsRes, modelsRes, compsRes] = await Promise.all([
          api.get('/brands'),
          api.get('/models'),
          api.get('/components'),
        ]);
        const brands = brandsRes.data || [];
        const models = modelsRes.data || [];
        const comps  = compsRes.data  || [];

        // indexation
        const brandById = new Map(brands.map(b => [String(b.id), b]));
        const modelById = new Map(models.map(m => [String(m.id), m]));
        const compById  = new Map(comps.map(c => [String(c.id), c]));

        const lines = Object.entries(cart).map(([id, qty]) => {
          qty = Number(qty) || 0;
          if (qty <= 0) return null;

          let line = null;

          if (id.startsWith('brand:')) {
            const bid = id.replace('brand:', '');
            const b = brandById.get(bid);
            if (!b) return null;
            line = {
              id,
              qty,
              price: Number(b?.pricingPrice) || 0,
              label: `${b?.brand || '—'}`,
              brand: String(b?.brand || '').toLowerCase(),
            };
          } else if (id.startsWith('model:')) {
            const mid = id.replace('model:', '');
            const m = modelById.get(mid);
            if (!m) return null;
            const brandName = m?.brand?.brand || '';
            const cap = m?.capacityGb ? ` (${m.capacityGb} Go)` : '';
            line = {
              id,
              qty,
              price: Number(m?.price) || 0,
              label: `${brandName} ${m?.model || ''}${cap} — Appareil complet`,
              brand: brandName.toLowerCase(),
            };
          } else if (id.startsWith('component:')) {
            const cid = id.replace('component:', '');
            const c = compById.get(cid);
            if (!c) return null;
            const brandName = c?.model?.brand?.brand || '';
            const mdl = c?.model?.model || '';
            line = {
              id,
              qty,
              price: Number(c?.unitPrice) || Number(c?.price) || 0,
              label: `${brandName} ${mdl} — ${c?.component}`,
              brand: brandName.toLowerCase(),
            };
          }

          if (!line) return null;

          // 🔗 rattache la note d'expertise (objet ou string) si fournie depuis /tarifs
          if (expertiseNotesFromNav && expertiseNotesFromNav[id] != null) {
            line.expertiseNote = expertiseNotesFromNav[id];
          }

          return line;
        }).filter(Boolean);

        if (!mounted) return;
        setSelectedLines(lines);

        // log vue recap + attache user si connecté
        const total = lines.reduce((s, l) => s + (l.price * l.qty), 0);
        const totalItems = lines.reduce((s, l) => s + l.qty, 0);

        const items = lines.map(l => ({
          id: String(l.id),
          qty: Number(l.qty) || 0,
          price: Number(l.price) || 0,
          label: l.label,
          brand: l.brand,
          ...(l.expertiseNote != null ? { expertiseNote: l.expertiseNote } : {}),
        }));

        syncCartToServer({
          items,
          totalQty: totalItems,
          totalAmount: total,
          context: 'recap',
        });

        if (isLoggedIn()) {
          // Assure l’attache du panier existant au compte connecté
          await attachCartToUser();
        }
      } catch (e) {
        if (mounted) setErr(e?.response?.data?.message || 'Impossible de charger le récapitulatif.');
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [expertiseNotesFromNav]);

  // Totaux
  const total = selectedLines.reduce((s, l) => s + (Number(l.price) || 0) * (Number(l.qty) || 0), 0);
  const totalItems = selectedLines.reduce((s, l) => s + (Number(l.qty) || 0), 0);

  const handleBackToTarifs = () => {
    // On reconstruit une map expertiseNotes à partir des lignes du récap
    const expertiseNotesMap = {};

    selectedLines.forEach((l) => {
      const note = l.expertiseNote;
      if (!note) return;

      if (typeof note === 'object') {
        const parcelsCount = String(
          note.parcelsCount ?? note.parcels ?? note.packagesCount ?? note.packages ?? note.colis ?? ''
        ).trim();
        const weightKg = String(
          note.weightKg ?? note.weight ?? ''
        ).trim();
        const piecesCount = String(
          note.piecesCount ?? note.pieces ?? ''
        ).trim();

        if (parcelsCount || weightKg || piecesCount) {
          // 🔁 On renvoie au format le plus récent (parcelsCount)
          expertiseNotesMap[l.id] = { parcelsCount, weightKg, piecesCount };
        }
      }
    });

    nav('/tarifs', {
      state: {
        summary: {
          // on renvoie les lignes pour reconstruire le récap sur /tarifs
          lines: selectedLines.map((l) => ({
            id: l.id,
            qty: l.qty,
            price: l.price,
            label: l.label,
            brand: l.brand,
          })),
          // et les notes d’expertise si on en a
          ...(Object.keys(expertiseNotesMap).length
            ? { expertiseNotes: expertiseNotesMap }
            : {}),
        },
        from: '/recap',
        editingTransactionId, // 👈 on garde le contexte d’édition
      },
    });
  };

  // 🧹 Reset du panier local (comme avant)
  const clearLocalCart = () => {
    writeCart({});
    writeCartMeta({ totalQty: 0, totalAmount: 0 });
  };

  // 💸 Création de la transaction (remis, avec expertiseNote)
  const postTransaction = async () => {
    const payload = {
      lines: selectedLines.map((l) => ({
        id: String(l.id),
        qty: Number(l.qty) || 0,
        ...(l.expertiseNote != null ? { expertiseNote: l.expertiseNote } : {}),
      })),
    };

    try {
      setSubmitting(true);

      // S’assurer que le panier est bien rattaché à l’utilisateur avant la création / édition
      if (isLoggedIn()) {
        try {
          await attachCartToUser();
        } catch {
          // silencieux, pas bloquant
        }
      }

      let res;

      if (editingTransactionId) {
        // 🔁 MODE ÉDITION : on met à jour la transaction existante
        res = await api.patch(`/transactions/${editingTransactionId}/replace`, payload);
      } else {
        // 🆕 MODE NORMAL : on crée une nouvelle transaction
        res = await api.post('/transactions', payload);
      }

      // 👍 après succès : on vide le panier local
      clearLocalCart();

      // on récupère éventuellement l'id de la transaction créée / éditée
      const txId = res?.data?.id || editingTransactionId || null;

      // ✅ Redirection vers la page "Mes envois"
      nav('/mes-envois', {
        replace: true,
        ...(txId ? { state: { highlightTransactionId: txId } } : {}),
      });
    } catch (e) {
      console.error('postTransaction error:', e?.response?.data || e);
      // on reste sur /recap et on affiche une erreur lisible
      setErr(e?.response?.data?.message || 'Impossible de valider votre envoi.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleValidate = async () => {
    if (!selectedLines.length) return;

    try {
      const items = selectedLines.map(l => ({
        id: String(l.id),
        qty: Number(l.qty) || 0,
        price: Number(l.price) || 0,
        label: l.label,
        brand: l.brand,
        ...(l.expertiseNote != null ? { expertiseNote: l.expertiseNote } : {}),
      }));
      await syncCartToServer({
        items,
        totalQty: totalItems,
        totalAmount: total,
        context: 'recap',
      });
    } catch {}

    if (!isLoggedIn()) {
      try {
        const payload = { redirectTo: '/recap' };
        sessionStorage.setItem('redirectAfterAuth', JSON.stringify(payload));
      } catch {}

      // 👇 Ici on force l’INSCRIPTION en premier
      setLoginOpen(false);
      setForgotOpen(false);
      setRegisterOpen(true);

      return;
    }

    await postTransaction();
  };

  const handleLoggedInFromLogin = async () => {
    setLoginOpen(false);
    // rattache immédiatement le panier pour que les logs ne restent pas "anonyme"
    try { await attachCartToUser(); } catch {}
    setToast({ open: true, titleKey: 'fancyToast.login.title', subtitleKey: 'fancyToast.login.subtitle', kind: 'success' });
    await postTransaction();
  };

  const handleLoggedInFromRegister = async () => {
    setRegisterOpen(false);
    try { await attachCartToUser(); } catch {}
    setToast({ open: true, titleKey: 'fancyToast.register.title', subtitleKey: 'fancyToast.register.subtitle', kind: 'celebration' });
    await postTransaction();
  };

  const openRegisterFromLogin = () => { setLoginOpen(false); setRegisterOpen(true); };
  const openLoginFromRegister = () => { setRegisterOpen(false); setLoginOpen(true); };
  const openForgotFromLogin   = () => { setLoginOpen(false); setForgotOpen(true); };

  const anyAuthOpen = loginOpen || registerOpen || forgotOpen;

  return (
    <>
      <Navbar />
      <Box sx={{ bgcolor: '#f7faf8', minHeight: '100vh' }}>
        <Container maxWidth="lg" sx={{ py: { xs: 2, md: 3 } }}>
          {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}

          <Paper elevation={0} sx={{ mb: 2, borderRadius: 3, overflow: 'hidden', border: '1px solid #e8efe9' }}>
            <Box sx={{ height: 6, bgcolor: GREEN }} />
            <Box sx={{ p: { xs: 2, md: 2.5 }, transition: 'filter 200ms ease', filter: anyAuthOpen ? 'blur(2px)' : 'none' }}>
              <Typography variant="h5" sx={{ fontWeight: 900, mb: 0.5 }}>
                {t('recap.header.title')}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('recap.header.subtitle')}
              </Typography>

              {selectedLines.length > 0 && (
                <Stack direction="row" spacing={1} sx={{ mt: 1 }} useFlexGap flexWrap="wrap">
                  <Chip size="small" label={`${t('recap.count.totalItems')} ${totalItems}`} sx={{ bgcolor: GREEN_BG }} />
                  <Chip size="small" label={`${t('recap.total')} ${money(total)} €`} sx={{ bgcolor: GREEN_BG }} />
                </Stack>
              )}
            </Box>
          </Paper>

          {loading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : selectedLines.length === 0 ? (
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
              <Typography sx={{ color: 'text.secondary' }}>
                {t('recap.empty.text')}
              </Typography>
              <Button
                component={RouterLink}
                to="/tarifs"
                variant="contained"
                sx={{ mt: 1.5, bgcolor: GREEN, '&:hover': { bgcolor: '#004992ff' } }}
              >
                {t('recap.empty.back')}
              </Button>
            </Paper>
          ) : (
            <Paper
              variant="outlined"
              sx={{
                p: { xs: 1.5, md: 2 },
                borderRadius: 3,
                borderColor: '#e8efe9',
                transition: 'filter 200ms ease',
                filter: anyAuthOpen ? 'blur(2px)' : 'none'
              }}
            >
              <TableContainer sx={{ mb: 2 }}>
                <Table size="small" aria-label={t('recap.table.aria')}>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700 }}>{t('recap.table.article')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{t('recap.table.unit')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{t('recap.table.qty')}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{t('recap.table.total')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedLines.map((l) => {
                      const pu = Number(l.price) || 0;
                      const q = Number(l.qty) || 0;
                      const displayLabel = formatRecapLabel(l, t);

                      return (
                        <TableRow key={l.id} hover>
                          <TableCell sx={{ maxWidth: 520 }}>
                            <Typography sx={{ fontWeight: 600 }}>{displayLabel}</Typography>
                          </TableCell>
                          <TableCell align="right">{money(pu)} €</TableCell>
                          <TableCell align="right">{q}</TableCell>
                          <TableCell align="right"><b>{money(pu * q)} €</b></TableCell>
                        </TableRow>
                      );
                    })}
                    <TableRow>
                      <TableCell colSpan={2}></TableCell>
                      <TableCell align="right"><b>{t('recap.table.total')}</b></TableCell>
                      <TableCell align="right"><b>{money(total)} €</b></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider sx={{ my: 2 }} />

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Button
                  variant="text"
                  onClick={handleBackToTarifs}
                  sx={{ color: 'text.secondary', textTransform: 'none', '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' } }}
                >
                  {t('recap.actions.editSelection')}
                </Button>

                <Button
                  variant="contained"
                  onClick={handleValidate}
                  disabled={!selectedLines.length || submitting}
                  sx={{ ml: 'auto', bgcolor: GREEN, fontWeight: 700, '&:hover': { bgcolor: '#004992ff' }, minWidth: 140 }}
                >
                  {submitting ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : t('recap.actions.validate')}
                </Button>
              </Box>
            </Paper>
          )}
        </Container>
      </Box>

      {/* 🔐 Auth modals */}
      <LoginModal
        open={loginOpen}
        onClose={() => setLoginOpen(false)}
        onLoggedIn={handleLoggedInFromLogin}
        onOpenRegister={openRegisterFromLogin}
        onOpenForgot={openForgotFromLogin}
      />
      <RegisterModal
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        onRegistered={handleLoggedInFromRegister}
        onOpenLogin={openLoginFromRegister}
      />
      <ForgotPasswordModal
        open={forgotOpen}
        onClose={() => { setForgotOpen(false); setLoginOpen(true); }}
      />

      <FancyToast
        open={toast.open}
        onClose={() => setToast((s) => ({ ...s, open: false }))}
        kind={toast.kind}
        titleKey={toast.titleKey}
        subtitleKey={toast.subtitleKey}
      />

      <Footer />
    </>
  );
}